import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChartCard, chartColors, type ChartSeries } from '../components/ChartCard';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { findSportMetric } from '../data/sportMetrics';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import type { ChartRange } from '../types';
import { buildSeries, sliceRange } from '../utils/series';
export function ProgressCharts() {
  const {
    metricId = 'body'
  } = useParams();
  const {
    t,
    tl
  } = useLocale();
  const metrics = useMetrics();
  const [range, setRange] = useState<ChartRange>('1y');
  const body = (id: string) => metrics.bodyMetrics.find(metric => metric.id === id);
  const sport = findSportMetric(decodeURIComponent(metricId));
  const series = useMemo<ChartSeries[] | null>(() => {
    if (metricId === 'body') {
      const ffm = body('ffm');
      const fat = body('bodyFat');
      const water = body('water');
      if (!ffm || !fat || !water) return null;
      return [{
        id: 'ffm',
        name: t('metrics.ffm'),
        kind: ffm.kind,
        points: sliceRange(buildSeries(ffm.value, 11, 0.04), range),
        color: chartColors.deep
      }, {
        id: 'bodyFat',
        name: t('metrics.bodyFat'),
        kind: fat.kind,
        points: sliceRange(buildSeries(fat.value, 23, 0.06), range),
        color: chartColors.gold
      }, {
        id: 'water',
        name: t('metrics.water'),
        kind: water.kind,
        points: sliceRange(buildSeries(water.value, 37, 0.03), range),
        color: chartColors.soft
      }];
    }
    if (metricId === 'weight') {
      const weight = body('weight');
      if (!weight) return null;
      return [{
        id: 'weight',
        name: t('metrics.weight'),
        kind: weight.kind,
        points: sliceRange(buildSeries(weight.value, 53, 0.03), range),
        color: chartColors.deep
      }];
    }
    if (sport) {
      return [{
        id: sport.id,
        name: tl(sport.label),
        kind: sport.kind,
        points: sliceRange(buildSeries(sport.value, 71, 0.05), range),
        color: chartColors.gold
      }];
    }
    return null;
  }, [metricId, range, sport, metrics.bodyMetrics, t, tl]);
  if (!series) {
    return <div className="px-2 pb-2" data-id="element-3134" data-mp="3ul0sym6ngqah">
        <PageHeader title={t('metrics.charts.title')} showBack data-id="element-3135" data-mp="3ulu2tw0wnvwu" />
        <EmptyState title={t('metrics.charts.missing.title')} body={t('metrics.charts.missing.body')} data-id="element-3136" data-mp="3ptsjxr98f6xu" />
      </div>;
  }
  const title = metricId === 'body' ? t('metrics.charts.body') : metricId === 'weight' ? t('metrics.weight') : tl(sport!.label);
  const question = metricId === 'body' ? t('metrics.charts.bodyQuestion') : metricId === 'weight' ? t('metrics.charts.weightQuestion') : t('metrics.charts.sportQuestion');
  return <div className="px-2 pb-2" data-id="element-3137" data-mp="25154td95sm8q">
      <PageHeader title={t('metrics.charts.title')} showBack data-id="element-3138" data-mp="252g7f9qvbny9" />

      <ChartCard title={title} question={question} series={series} range={range} onRangeChange={setRange} data-id="element-3139" data-mp="290nd99jsn4uo" />

      {metricId === 'body' && <p className="mt-2 px-1 text-[11px] leading-snug text-muted" data-id="element-3140" data-mp="2izlar1z768pt">
          {t('metrics.charts.bodyNote')}
        </p>}
    </div>;
}